import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { useGetAllInvoiceQuery, useGetDriverQuery, useCreateDriverPaymentMutation } from "../../../Api/attoDeskApi";
import { TransferType, getTripStatus } from "../../../Core/Enum/enum";
import { IEmailTemplate } from "../../../Api/Interface/api.interface";
import { useGetEmailTemplateQuery } from "../../../Api/attoDeskApi";
import axios from "axios";
import { useNotifier } from "../../../Core/Notifier";
import { useNavigate } from "react-router-dom";
import CustomCheckbox from "../../../Components/Checkbox.tsx";

// Icons
import DownloadIcon from "@mui/icons-material/Download";
import EmailIcon from "@mui/icons-material/Email";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DirectionsBusOutlinedIcon from "@mui/icons-material/DirectionsBusOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import * as XLSX from "xlsx";

dayjs.extend(isBetween);

// ── Availability helpers ────────────────────────────────────────────────────────

/** Parse "HH:mm" → total minutes since midnight. Returns null for empty / invalid. */
function parseTimeMins24(hhmm: string): number | null {
  const m = (hhmm || "").match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  return parseInt(m[1]) * 60 + parseInt(m[2]);
}

/** Convert minutes-since-midnight → "h:mm AM/PM" display string. */
function minsToAmPm(mins: number): string {
  const h    = Math.floor(mins / 60);
  const m    = mins % 60;
  const ampm = h >= 12 ? "PM" : "AM";
  const hh   = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hh}:${String(m).padStart(2, "0")} ${ampm}`;
}

interface ActiveSlot { startTime: string; endTime: string; startMins: number; endMins: number; }

/**
 * Returns ONLY slots that have both valid startTime and endTime in "HH:mm" format.
 * This handles the Edit-form behaviour where all 7 days are stored but
 * some have empty-string timeSlots (e.g. {startTime: "", endTime: ""}).
 */
function getActiveSlots(driver: any, dayName: string): ActiveSlot[] {
  const avail: any[] = Array.isArray(driver?.availability) ? driver.availability : [];
  if (avail.length === 0) return [];
  const dayAvail = avail.find((a: any) => (a.day || "").toLowerCase() === dayName.toLowerCase());
  if (!dayAvail || !Array.isArray(dayAvail.timeSlots)) return [];
  return dayAvail.timeSlots
    .map((s: any) => ({
      startTime: s.startTime || "",
      endTime:   s.endTime   || "",
      startMins: parseTimeMins24(s.startTime) ?? -1,
      endMins:   parseTimeMins24(s.endTime)   ?? -1,
    }))
    .filter((s: ActiveSlot) => s.startMins >= 0 && s.endMins >= 0);
}

interface AvailCheck {
  /** true = trip is within hours (or no schedule configured) */
  valid:       boolean;
  /** human-readable reason shown in tooltip when invalid */
  reason:      string;
  /** active slots for this day – used for display */
  slots:       ActiveSlot[];
  /** false when the driver has no configured hours for this day */
  hasSchedule: boolean;
}

function checkTripAvailability(driver: any, trip: Trip): AvailCheck {
  const depDt = trip.departureDateTime;
  const retDt = trip.returnDateTime;

  if (!depDt || !dayjs(depDt).isValid()) {
    return { valid: true, reason: "", slots: [], hasSchedule: false };
  }

  const dayName    = dayjs(depDt).format("dddd");          // e.g. "Monday"
  const activeSlots = getActiveSlots(driver, dayName);

  // No valid slots for this day → schedule not configured for the day, no restriction
  if (activeSlots.length === 0) {
    return { valid: true, reason: "", slots: [], hasSchedule: false };
  }

  const pickupMins  = dayjs(depDt).hour() * 60 + dayjs(depDt).minute();
  const dropoffMins = retDt && dayjs(retDt).isValid()
    ? dayjs(retDt).hour() * 60 + dayjs(retDt).minute()
    : null;

  for (const slot of activeSlots) {
    if (pickupMins >= slot.startMins && (dropoffMins === null || dropoffMins <= slot.endMins)) {
      return { valid: true, reason: "", slots: activeSlots, hasSchedule: true };
    }
  }

  // Trip does not fit any slot → conflict
  const pickupStr  = minsToAmPm(pickupMins);
  const dropoffStr = dropoffMins !== null ? `–${minsToAmPm(dropoffMins)}` : "";
  const slotDescs  = activeSlots.map((s) => `${s.startTime}–${s.endTime}`).join(", ");

  return {
    valid:       false,
    reason:      `Trip time (${pickupStr}${dropoffStr}) is outside working hours on ${dayName} (${slotDescs})`,
    slots:       activeSlots,
    hasSchedule: true,
  };
}

// ── Types ──────────────────────────────────────────────────────────────────────
interface Trip {
  quoteWorkOrderNo: string;
  productName: string;
  // Pickup
  departureAddress: string;
  departureDateTime: string;
  // Drop-off
  destinationAddress: string;
  returnDateTime: string;
  // Route labels (city names)
  transferTypeFrom: string;
  transferTypeTo: string;
  // Type
  transferType: string;
  // Invoice
  InvoiceNo: string;
  work_order_id?: string | number;
  // Payment status (from invoice.quoteWorkOrderStatus)
  isPaid: boolean;
  isCancelled: boolean;
}

interface DriverTripData {
  driverId: string;
  driverName: string;
  driverFirstName: string;
  driverLastName: string;
  driverEmail: string;
  driverMobileNum?: string;
  trips: Trip[];
}

// ── Design tokens ──────────────────────────────────────────────────────────────
const BRAND = "#2f3891";
const BRAND_DARK = "#2f3891";
const BORDER = "#e2e8f0";

// ── Helpers ────────────────────────────────────────────────────────────────────
const cap = (s?: string | null) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";

const initials = (a?: string, b?: string) =>
  `${(a || "?")[0]}${(b || "")[0] || ""}`.toUpperCase();

const fmt = (dt: string, format = "DD MMM YYYY") =>
  dt && dayjs(dt).isValid() ? dayjs(dt).format(format) : null;

const fmtTime = (dt: string) =>
  dt && dayjs(dt).isValid() ? dayjs(dt).format("h:mm A") : null;

const transferLabel = (val: string) => {
  const map: Record<string, { label: string; color: string; bg: string }> = {
    "0": { label: "One Way",     color: "#2563eb", bg: "#dbeafe" },
    "1": { label: "Round Trip",  color: "#2f3891", bg: "#ede9fe" },
    "2": { label: "Multi Dest.", color: "#0f766e", bg: "#ccfbf1" },
  };
  return map[String(val)] ?? { label: val || "—", color: "#64748b", bg: "#f1f5f9" };
};

// Delegates to the shared getTripStatus helper in enum.ts
// Logic: past trip → Completed | paid + future → Confirmed | otherwise → Pending
const tripStatus = (dt: string, isPaid = false, isCancelled = false) =>
  getTripStatus(dt, isPaid, isCancelled);

// ── Shared table styles ────────────────────────────────────────────────────────
const thSx = {
  fontWeight: 700, fontSize: 11, color: "#64748b",
  textTransform: "uppercase" as const, letterSpacing: "0.06em",
  py: 1.25, px: 1.5, borderBottom: `1px solid ${BORDER}`, bgcolor: "#f8fafc",
  whiteSpace: "nowrap" as const,
};
const tdSx = {
  py: 1.5, px: 1.5, borderBottom: "1px solid #f1f5f9",
  fontSize: 12, color: "#1e293b", verticalAlign: "top" as const,
};

// ── Route cell: full pickup / drop-off display ─────────────────────────────────
const RouteCell: React.FC<{ trip: Trip }> = ({ trip }) => {
  const isRound = trip.transferType === "1";

  const pickupAddr = trip.departureAddress;
  const pickupDate = fmt(trip.departureDateTime);
  const pickupTime = fmtTime(trip.departureDateTime);

  const dropAddr   = trip.destinationAddress;
  const dropDate   = fmt(trip.returnDateTime);
  const dropTime   = fmtTime(trip.returnDateTime);

  const hasPickup  = pickupAddr || pickupDate;
  const hasDrop    = dropAddr   || dropDate;

  if (!hasPickup && !hasDrop) {
    return <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>—</Typography>;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {/* ── Pickup leg ── */}
      {hasPickup && (
        <Box sx={{ bgcolor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 1.5, px: 1.25, py: 0.75 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.4 }}>
            <FmdGoodOutlinedIcon sx={{ fontSize: 11, color: "#16a34a" }} />
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#16a34a", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Pickup
            </Typography>
          </Box>
          {pickupAddr && (
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#0f172a", lineHeight: 1.35 }}>
              {pickupAddr}
            </Typography>
          )}
          {(pickupDate || pickupTime) && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.4 }}>
              <AccessTimeOutlinedIcon sx={{ fontSize: 10, color: "#64748b" }} />
              <Typography sx={{ fontSize: 11, color: "#374151" }}>{pickupDate}</Typography>
              {pickupTime && (
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: BRAND }}>{pickupTime}</Typography>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* ── Drop-off leg ── */}
      {hasDrop && (
        <Box sx={{ bgcolor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 1.5, px: 1.25, py: 0.75 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.4 }}>
            <FmdGoodOutlinedIcon sx={{ fontSize: 11, color: "#dc2626" }} />
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#dc2626", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              {isRound ? "Return / Drop-off" : "Drop-off"}
            </Typography>
          </Box>
          {dropAddr && (
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#0f172a", lineHeight: 1.35 }}>
              {dropAddr}
            </Typography>
          )}
          {(dropDate || dropTime) && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.4 }}>
              <AccessTimeOutlinedIcon sx={{ fontSize: 10, color: "#64748b" }} />
              <Typography sx={{ fontSize: 11, color: "#374151" }}>{dropDate}</Typography>
              {dropTime && (
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#2f3891" }}>{dropTime}</Typography>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

// ── Print view (hidden DOM node used by html2pdf) ──────────────────────────────
const PrintView: React.FC<{ drivers: DriverTripData[] }> = ({ drivers }) => (
  <div style={{ fontFamily: "Arial, sans-serif", fontSize: 12, color: "#1e293b", padding: 24 }}>
    <div style={{ textAlign: "center", marginBottom: 24 }}>
      <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: "#0f172a" }}>TJBus — Driver Schedule</h1>
      <p style={{ fontSize: 12, color: "#64748b", margin: "4px 0 0" }}>
        Generated: {dayjs().format("DD MMM YYYY h:mm A")}
      </p>
    </div>

    {drivers.map((driver, di) => (
      <div key={di} style={{ marginBottom: 32, pageBreakInside: "avoid" }}>
        {/* Driver header */}
        <div style={{
          background: "#eef2ff", border: "1px solid #c7d2fe", borderRadius: 6,
          padding: "10px 16px", marginBottom: 12,
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#2f3891" }}>{driver.driverName}</div>
            {driver.driverEmail && (
              <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{driver.driverEmail}</div>
            )}
            {driver.driverMobileNum && (
              <div style={{ fontSize: 11, color: "#64748b" }}>📞 {driver.driverMobileNum}</div>
            )}
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#2f3891" }}>
            {driver.trips.length} trip{driver.trips.length !== 1 ? "s" : ""}
          </div>
        </div>

        {/* Trips table */}
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
          <thead>
            <tr style={{ background: "#f8fafc" }}>
              {["#", "Invoice", "Bus / Service", "Transfer Type", "Route (From → To)", "Pickup Address", "Pickup Date & Time", "Drop-off Address", "Drop-off Date & Time", "Status"].map((h) => (
                <th key={h} style={{
                  border: "1px solid #e2e8f0", padding: "6px 8px",
                  fontWeight: 700, fontSize: 10, color: "#64748b",
                  textAlign: "left", textTransform: "uppercase", letterSpacing: "0.04em",
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {driver.trips.map((trip, ti) => {
              const tt = transferLabel(trip.transferType);
              const st = tripStatus(trip.departureDateTime, trip.isPaid, trip.isCancelled);
              const isToday = dayjs(trip.departureDateTime).isSame(dayjs(), "day");

              return (
                <tr key={ti} style={{ background: isToday ? "#fffbeb" : ti % 2 === 0 ? "#fff" : "#fafafa" }}>
                  <td style={{ border: "1px solid #e2e8f0", padding: "6px 8px", color: "#94a3b8", width: 24 }}>{ti + 1}</td>
                  <td style={{ border: "1px solid #e2e8f0", padding: "6px 8px", fontWeight: 700, color: "#2f3891" }}>{trip.InvoiceNo || "—"}</td>
                  <td style={{ border: "1px solid #e2e8f0", padding: "6px 8px" }}>{trip.productName || "—"}</td>
                  <td style={{ border: "1px solid #e2e8f0", padding: "6px 8px" }}>
                    <span style={{
                      background: tt.bg, color: tt.color, fontWeight: 700, fontSize: 10,
                      padding: "2px 6px", borderRadius: 4,
                    }}>{tt.label}</span>
                  </td>
                  <td style={{ border: "1px solid #e2e8f0", padding: "6px 8px", color: "#475569" }}>
                    {trip.transferTypeFrom && trip.transferTypeTo
                      ? `${trip.transferTypeFrom} → ${trip.transferTypeTo}`
                      : "—"}
                  </td>
                  {/* Pickup */}
                  <td style={{ border: "1px solid #e2e8f0", padding: "6px 8px", color: "#0f172a" }}>
                    {trip.departureAddress || "—"}
                  </td>
                  <td style={{ border: "1px solid #e2e8f0", padding: "6px 8px", color: "#0f172a", whiteSpace: "nowrap" }}>
                    {trip.departureDateTime && dayjs(trip.departureDateTime).isValid()
                      ? dayjs(trip.departureDateTime).format("DD MMM YYYY h:mm A")
                      : "—"}
                  </td>
                  {/* Drop-off */}
                  <td style={{ border: "1px solid #e2e8f0", padding: "6px 8px", color: "#0f172a" }}>
                    {trip.destinationAddress || "—"}
                  </td>
                  <td style={{ border: "1px solid #e2e8f0", padding: "6px 8px", color: "#0f172a", whiteSpace: "nowrap" }}>
                    {trip.returnDateTime && dayjs(trip.returnDateTime).isValid()
                      ? dayjs(trip.returnDateTime).format("DD MMM YYYY h:mm A")
                      : "—"}
                  </td>
                  {/* Status */}
                  <td style={{ border: "1px solid #e2e8f0", padding: "6px 8px" }}>
                    <span style={{
                      background: st.bg, color: st.color, fontWeight: 700, fontSize: 10,
                      padding: "2px 6px", borderRadius: 4,
                    }}>{st.label}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    ))}
  </div>
);

// ── Main component ─────────────────────────────────────────────────────────────
const DriverTripsComponent: React.FC = () => {
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const API_URL = process.env.REACT_APP_API_URL;
  const printRef = useRef<HTMLDivElement>(null);

  const { data: invoiceData, isLoading, isError } = useGetAllInvoiceQuery({
    searchText: "", id: "",
    quoteWorkOrderCustomerId: "", quoteWorkOrderCustomerProjectId: "",
  });
  const { data: driverData } = useGetDriverQuery({ searchText: "", id: "" });
  const { data: emailTemplateData } = useGetEmailTemplateQuery({ searchText: "" });

  const [createDriverPayment] = useCreateDriverPaymentMutation();

  const [driverTripsList, setDriverTripsList] = useState<DriverTripData[]>([]);
  const [selectedTrips, setSelectedTrips] = useState<Record<string, boolean>>({});
  const [drivers, setDrivers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState<string>("");
  const [dateFilter, setDateFilter] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [collapsedDrivers, setCollapsedDrivers] = useState<Record<string, boolean>>({});

  // ── Complete Trip modal state ────────────────────────────────────────────────
  const [completeTripOpen, setCompleteTripOpen] = useState(false);
  const [completingTrip, setCompletingTrip] = useState<{ trip: Trip; driverData: DriverTripData } | null>(null);
  const [ctHourlyRate, setCtHourlyRate] = useState<string>("32");
  const [ctHoursWorked, setCtHoursWorked] = useState<string>("0");
  const [ctTips, setCtTips] = useState<string>("0");
  const [ctDriverComments, setCtDriverComments] = useState<string>("");
  const [ctAdminComments, setCtAdminComments] = useState<string>("");
  const [ctCustomerFeedback, setCtCustomerFeedback] = useState<string>("");
  const [ctSaving, setCtSaving] = useState(false);

  const ctBaseSalary = useMemo(() => {
    const hours = parseFloat(ctHoursWorked) || 0;
    const rate  = parseFloat(ctHourlyRate)  || 0;
    return Math.round(hours * rate * 100) / 100;
  }, [ctHoursWorked, ctHourlyRate]);

  const ctTotal = useMemo(() => {
    const tips = parseFloat(ctTips) || 0;
    return Math.round((ctBaseSalary + tips) * 100) / 100;
  }, [ctBaseSalary, ctTips]);

  const openCompleteTrip = (trip: Trip, driverInfo: DriverTripData) => {
    const dep = trip.departureDateTime;
    const ret = trip.returnDateTime;
    let autoHours = "0";
    if (dep && ret && dayjs(dep).isValid() && dayjs(ret).isValid()) {
      const mins = dayjs(ret).diff(dayjs(dep), "minute");
      autoHours = String(Math.max(0, Math.round((mins / 60) * 100) / 100));
    }
    setCompletingTrip({ trip, driverData: driverInfo });
    setCtHourlyRate("32");
    setCtHoursWorked(autoHours);
    setCtTips("0");
    setCtDriverComments("");
    setCtAdminComments("");
    setCtCustomerFeedback("");
    setCompleteTripOpen(true);
  };

  const handleCompleteTripSave = async () => {
    if (!completingTrip) return;
    setCtSaving(true);
    try {
      const result: any = await createDriverPayment({
        driver_id:         Number(completingTrip.driverData.driverId),
        work_order_id:     completingTrip.trip.work_order_id ? Number(completingTrip.trip.work_order_id) : null,
        invoice_no:        completingTrip.trip.InvoiceNo || null,
        driver_name:       completingTrip.driverData.driverName,
        trip_date:         completingTrip.trip.departureDateTime || null,
        hours_worked:      parseFloat(ctHoursWorked) || 0,
        hourly_rate:       parseFloat(ctHourlyRate) || 32,
        base_salary:       ctBaseSalary,
        tips:              parseFloat(ctTips) || 0,
        total_payment:     ctTotal,
        driver_comments:   ctDriverComments.trim()   || null,
        admin_comments:    ctAdminComments.trim()    || null,
        customer_feedback: ctCustomerFeedback.trim() || null,
      });
      if (result?.error || result?.data?.status === false) {
        showErrorMessage(result?.data?.message || result?.error?.data?.message || "Failed to save payment record.");
      } else {
        showMessage("Trip completed & payment record saved.");
        setCompleteTripOpen(false);
        setCompletingTrip(null);
      }
    } catch {
      showErrorMessage("An unexpected error occurred. Please try again.");
    }
    setCtSaving(false);
  };

  const emailTemplateList: IEmailTemplate[] = useMemo(() => {
    if (emailTemplateData && Array.isArray(emailTemplateData.data))
      return emailTemplateData.data as IEmailTemplate[];
    return [];
  }, [emailTemplateData]);

  // ── Build driver → trips map ────────────────────────────────────────────────
  useEffect(() => {
    if (!invoiceData || !Array.isArray(invoiceData.data)) return;

    const tripsMap:  Record<string, Trip[]>   = {};
    const nameMap:   Record<string, string>   = {};
    const firstMap:  Record<string, string>   = {};
    const lastMap:   Record<string, string>   = {};
    const emailMap:  Record<string, string>   = {};
    const phoneMap:  Record<string, string>   = {};

    invoiceData.data.forEach((invoice: any) => {
      if (!Array.isArray(invoice.drivers) || !Array.isArray(invoice.quoteWorkOrderSale)) return;

      invoice.drivers.forEach((driver: any) => {
        const key = String(driver.id);
        nameMap[key]  = `${driver.driverFirstName || ""} ${driver.driverLastName || ""}`.trim();
        firstMap[key] = driver.driverFirstName || "";
        lastMap[key]  = driver.driverLastName  || "";
        emailMap[key] = driver.driverEmail     || "";
        phoneMap[key] = driver.driverMobileNum || "";

        const invoiceIsPaid      = invoice.quoteWorkOrderStatus === 0;
        const invoiceIsCancelled = invoice.quoteWorkOrderStatus === 1;

        invoice.quoteWorkOrderSale.forEach((sale: any) => {
          // Support both new format (pickups/dropoffs arrays) and legacy flat fields
          const firstPickup  = Array.isArray(sale?.pickups)  && sale.pickups.length  > 0 ? sale.pickups[0]                          : null;
          const lastDropoff  = Array.isArray(sale?.dropoffs) && sale.dropoffs.length > 0 ? sale.dropoffs[sale.dropoffs.length - 1]   : null;

          const depDt   = firstPickup?.dateTime || sale.departureDateTime  || "";
          const retDt   = lastDropoff?.dateTime || sale.returnDateTime     || "";
          const depAddr = firstPickup
            ? [firstPickup.address, firstPickup.city].filter(Boolean).join(", ")
            : sale.departureAddress || "";
          const destAddr = lastDropoff
            ? [lastDropoff.address, lastDropoff.city].filter(Boolean).join(", ")
            : sale.destinationAddress || "";

          const trip: Trip = {
            quoteWorkOrderNo:   invoice.quoteWorkOrderNo || "",
            productName:        sale.productName         || "",
            departureAddress:   depAddr,
            departureDateTime:  depDt,
            destinationAddress: destAddr,
            returnDateTime:     retDt,
            transferTypeFrom:   sale.transferTypeFrom    || firstPickup?.city  || "",
            transferTypeTo:     sale.transferTypeTo      || lastDropoff?.city  || "",
            transferType:       String(sale.transferType ?? ""),
            InvoiceNo:          invoice.InvoiceNo        || "",
            work_order_id:      invoice.work_order_id    || invoice.id,
            isPaid:             invoiceIsPaid,
            isCancelled:        invoiceIsCancelled,
          };
          tripsMap[key] = tripsMap[key] ? [...tripsMap[key], trip] : [trip];
        });
      });
    });

    const grouped: DriverTripData[] = Object.keys(tripsMap).map((id) => ({
      driverId:       id,
      driverName:     nameMap[id]  || "Unknown",
      driverFirstName:firstMap[id] || "",
      driverLastName: lastMap[id]  || "",
      driverEmail:    emailMap[id] || "",
      driverMobileNum:phoneMap[id] || "",
      trips: tripsMap[id],
    }));

    setDriverTripsList(grouped);
  }, [invoiceData]);

  useEffect(() => {
    if (driverData && Array.isArray(driverData.data))
      setDrivers(driverData.data.filter((d: any) => d.driverUserRole === "driver"));
  }, [driverData]);

  // ── Filtering ───────────────────────────────────────────────────────────────
  const filteredDriverTrips = useMemo(() => {
    return driverTripsList
      .filter((d) => !selectedDriver || d.driverId === selectedDriver)
      .map((d) => ({
        ...d,
        trips: d.trips.filter((trip) => {
          if (!dateFilter) return true;
          const td = dayjs(trip.departureDateTime);
          if (!td.isValid()) return true;
          const today    = dayjs().startOf("day");
          const todayEnd = dayjs().endOf("day");
          switch (dateFilter) {
            case "today":
              return td.isSame(today, "day");
            case "tomorrow":
              return td.isSame(today.add(1, "day"), "day");
            case "this_week":
              return td.isBetween(today, today.endOf("week"), null, "[]");
            case "next_week": {
              const nwStart = today.add(1, "week").startOf("week");
              return td.isBetween(nwStart, nwStart.endOf("week"), null, "[]");
            }
            case "this_month":
              return td.isBetween(today, today.endOf("month"), null, "[]");
            case "next_month": {
              const nmStart = today.add(1, "month").startOf("month");
              return td.isBetween(nmStart, nmStart.endOf("month"), null, "[]");
            }
            case "custom":
              return startDate && endDate
                ? td.isBetween(dayjs(startDate), dayjs(endDate), null, "[]")
                : true;
            default: return true;
          }
        }),
      }))
      .filter((d) => d.trips.length > 0);
  }, [driverTripsList, selectedDriver, dateFilter, startDate, endDate]);

  // ── Summary counts ──────────────────────────────────────────────────────────
  const totalTrips    = filteredDriverTrips.reduce((s, d) => s + d.trips.length, 0);
  const todayTrips    = filteredDriverTrips.reduce((s, d) => s + d.trips.filter((t) => dayjs(t.departureDateTime).isSame(dayjs(), "day")).length, 0);
  const upcomingTrips = filteredDriverTrips.reduce((s, d) => s + d.trips.filter((t) => { const dep = dayjs(t.departureDateTime); return dep.isValid() && dep.isAfter(dayjs()); }).length, 0);

  // Count trips that are OUTSIDE configured working hours (not just "no schedule set")
  const conflictCount = filteredDriverTrips.reduce((s, d) => {
    const fullDriver = drivers.find((dr: any) => String(dr.id) === d.driverId);
    if (!fullDriver) return s;
    return s + d.trips.filter((t) => {
      const c = checkTripAvailability(fullDriver, t);
      return !c.valid && c.hasSchedule;
    }).length;
  }, 0);

  // ── Checkbox helpers ────────────────────────────────────────────────────────
  const tk = (driverId: string, trip: Trip, idx: number) =>
    `${driverId}-${trip.quoteWorkOrderNo}-${trip.productName}-${idx}`;

  const handleTripCheckbox     = (key: string) => setSelectedTrips((p) => ({ ...p, [key]: !p[key] }));
  const handleSelectAll        = (driverId: string, trips: Trip[]) => {
    const all = trips.every((t, i) => selectedTrips[tk(driverId, t, i)]);
    const updated = { ...selectedTrips };
    trips.forEach((t, i) => { updated[tk(driverId, t, i)] = !all; });
    setSelectedTrips(updated);
  };
  const anySelected            = (driverId: string, trips: Trip[]) =>
    trips.some((t, i) => selectedTrips[tk(driverId, t, i)]);

  // ── Email ───────────────────────────────────────────────────────────────────
  const sendEmailForDriver = async (driver: DriverTripData) => {
    const selected = driver.trips.filter((t, i) => selectedTrips[tk(driver.driverId, t, i)]);
    if (!selected.length) { alert(`No trips selected for ${driver.driverName}`); return; }
    const tmpl = emailTemplateList.find((t) => t.template_name === "Trip Assignment");
    if (!tmpl) { alert("Trip Assignment email template not found"); return; }

    setLoading(true);
    const header = `<p>Dear ${driver.driverName},</p><p>You have been assigned the following trip(s). Please find details below:</p>`;
    const footer = `<p>For further assistance, contact us.</p><p>Best regards,<br/>TJ Bus — 647 710-0532 | tjbusline@gmail.com</p>`;
    const body = selected.map((trip, i) => `
<p><strong>Trip ${i + 1}</strong></p>
<p>
  <strong>Invoice:</strong> ${trip.InvoiceNo || "—"}<br/>
  <strong>Bus / Service:</strong> ${trip.productName || "—"}<br/>
  <strong>Transfer Type:</strong> ${transferLabel(trip.transferType).label}<br/>
  <strong>Route:</strong> ${trip.transferTypeFrom || "—"} → ${trip.transferTypeTo || "—"}<br/>
  <strong>Pickup Address:</strong> ${trip.departureAddress || "—"}<br/>
  <strong>Pickup Time:</strong> ${dayjs(trip.departureDateTime).isValid() ? dayjs(trip.departureDateTime).format("DD MMM YYYY h:mm A") : "—"}<br/>
  <strong>Drop-off Address:</strong> ${trip.destinationAddress || "—"}<br/>
  <strong>Drop-off Time:</strong> ${trip.returnDateTime && dayjs(trip.returnDateTime).isValid() ? dayjs(trip.returnDateTime).format("DD MMM YYYY h:mm A") : "—"}
</p>`).join("\n\n<hr/>\n\n");

    try {
      const res = await axios.post(`${API_URL}/send-email`,
        { to: driver.driverEmail, subject: tmpl.subject, message: header + body + footer },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` } }
      );
      if (res.status === 200) { showMessage("Email sent successfully"); setSelectedTrips({}); }
    } catch { showErrorMessage("Failed to send email."); }
    setLoading(false);
  };

  // ── PDF export ──────────────────────────────────────────────────────────────
  const [pdfLoading, setPdfLoading] = useState(false);
  const handleDownloadPDF = async () => {
    setPdfLoading(true);
    try {
      const params: Record<string, string> = {};
      if (startDate)       params.start_date = startDate;
      if (endDate)         params.end_date   = endDate;
      if (selectedDriver)  params.driver_id  = selectedDriver;
      const response = await axios.get(`${API_URL}/pdf/tour-report`, {
        params, responseType: "blob",
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      });
      const url  = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `tour-report-${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch { /* silent */ } finally { setPdfLoading(false); }
  };

  // ── Excel export ────────────────────────────────────────────────────────────
  const handleExportToExcel = () => {
    const data = filteredDriverTrips.flatMap((driver) =>
      driver.trips.map((trip, i) => ({
        "#":              i + 1,
        "Driver":         driver.driverName,
        "Driver Email":   driver.driverEmail,
        "Driver Phone":   driver.driverMobileNum || "",
        "Invoice No":     trip.InvoiceNo || "",
        "Bus / Service":  trip.productName || "",
        "Transfer Type":  transferLabel(trip.transferType).label,
        "Route From":     trip.transferTypeFrom || "",
        "Route To":       trip.transferTypeTo || "",
        "Pickup Address": trip.departureAddress || "",
        "Pickup Date":    trip.departureDateTime && dayjs(trip.departureDateTime).isValid() ? dayjs(trip.departureDateTime).format("DD MMM YYYY") : "",
        "Pickup Time":    trip.departureDateTime && dayjs(trip.departureDateTime).isValid() ? dayjs(trip.departureDateTime).format("h:mm A") : "",
        "Drop Address":   trip.destinationAddress || "",
        "Drop Date":      trip.returnDateTime && dayjs(trip.returnDateTime).isValid() ? dayjs(trip.returnDateTime).format("DD MMM YYYY") : "",
        "Drop Time":      trip.returnDateTime && dayjs(trip.returnDateTime).isValid() ? dayjs(trip.returnDateTime).format("h:mm A") : "",
        "Status":         tripStatus(trip.departureDateTime, trip.isPaid, trip.isCancelled).label,
      }))
    );
    const ws = XLSX.utils.json_to_sheet(data);
    // Auto-column widths
    const colWidths = Object.keys(data[0] || {}).map((k) => ({
      wch: Math.max(k.length, ...data.map((r: any) => String(r[k] || "").length)) + 2,
    }));
    ws["!cols"] = colWidths;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Driver Schedule");
    XLSX.writeFile(wb, `driver_schedule_${dayjs().format("YYYYMMDD")}.xlsx`);
  };

  const toggleDriver = (id: string) =>
    setCollapsedDrivers((p) => ({ ...p, [id]: !p[id] }));

  // ── Loading / error states ──────────────────────────────────────────────────
  if (isLoading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={32} sx={{ color: BRAND }} />
      </Box>
    );
  if (isError)
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <Typography sx={{ color: "#ef4444", fontSize: 14 }}>Error loading driver schedule.</Typography>
      </Box>
    );

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Hidden print DOM — captured by html2pdf, invisible on screen */}
      <Box ref={printRef} sx={{ position: "absolute", left: -9999, top: 0, width: "100%", visibility: "hidden" }}>
        <PrintView drivers={filteredDriverTrips} />
      </Box>

      <Box sx={{ p: { xs: 1.5, md: 2 } }}>

        {/* ── Page header ── */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 2, background: "linear-gradient(135deg,#2f3891,#2563eb)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <LocalShippingOutlinedIcon sx={{ color: "#fff", fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 18, color: "#0f172a", lineHeight: 1 }}>Driver Schedule</Typography>
              <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.3 }}>
                {filteredDriverTrips.length} driver{filteredDriverTrips.length !== 1 ? "s" : ""} · {totalTrips} trip{totalTrips !== 1 ? "s" : ""}
              </Typography>
            </Box>
          </Box>
          {/* Export */}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button size="small"
              startIcon={pdfLoading ? <CircularProgress size={13} color="inherit" /> : <DownloadIcon sx={{ fontSize: 14 }} />}
              onClick={handleDownloadPDF} disabled={pdfLoading}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, fontSize: 12, bgcolor: "#1e293b", color: "#fff", px: 1.5, py: 0.75, "&:hover": { bgcolor: "#0f172a" } }}>
              {pdfLoading ? "Generating…" : "PDF"}
            </Button>
            <Button size="small" startIcon={<DownloadIcon sx={{ fontSize: 14 }} />} onClick={handleExportToExcel}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, fontSize: 12, bgcolor: "#16a34a", color: "#fff", px: 1.5, py: 0.75, "&:hover": { bgcolor: "#15803d" } }}>
              Excel
            </Button>
          </Box>
        </Box>

        {/* ── Summary stat cards ── */}
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          {[
            { label: "Total Trips",  value: totalTrips,                    icon: "🚌", color: BRAND,     bg: "#eef2ff" },
            { label: "Today",        value: todayTrips,                    icon: "📅", color: "#d97706", bg: "#fef3c7" },
            { label: "Upcoming",     value: upcomingTrips,                 icon: "⏱",  color: "#2563eb", bg: "#dbeafe" },
            { label: "Drivers",      value: filteredDriverTrips.length,    icon: "👤", color: "#0f766e", bg: "#ccfbf1" },
            { label: "Hour Conflicts", value: conflictCount,               icon: "⚠️",  color: "#dc2626", bg: "#fef2f2" },
          ].map((s) => (
            <Grid item xs={6} sm={3} key={s.label}>
              <Box sx={{ bgcolor: s.bg, border: `1px solid ${s.color}22`, borderRadius: 2.5, p: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{ fontSize: 22, lineHeight: 1 }}>{s.icon}</Box>
                <Box>
                  <Typography sx={{ fontSize: 20, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</Typography>
                  <Typography sx={{ fontSize: 11, color: "#64748b", mt: 0.25 }}>{s.label}</Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* ── Filters ── */}
        <Card sx={{ borderRadius: 3, boxShadow: "0 1px 6px rgba(0,0,0,0.06)", border: `1px solid ${BORDER}`, mb: 2.5 }}>
          <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${BORDER}`, display: "flex", alignItems: "center", gap: 1 }}>
            <FilterListIcon sx={{ fontSize: 15, color: "#64748b" }} />
            <Typography sx={{ fontWeight: 700, fontSize: 11, color: "#475569", textTransform: "uppercase", letterSpacing: "0.07em" }}>Filters</Typography>
          </Box>
          <Box sx={{ p: 2, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-end" }}>
            {/* Driver */}
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#64748b", mb: 0.5, textTransform: "uppercase", letterSpacing: "0.05em" }}>Driver</Typography>
              <Select size="small" value={selectedDriver} onChange={(e) => setSelectedDriver(String(e.target.value))} displayEmpty
                sx={{ minWidth: 200, borderRadius: 2, fontSize: 13, height: 36, bgcolor: "#fff", "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER } }}>
                <MenuItem value=""><em>All Drivers</em></MenuItem>
                {drivers.map((d: any) => (
                  <MenuItem key={d.id} value={String(d.id)}>{cap(d.driverFirstName)} {cap(d.driverLastName)}</MenuItem>
                ))}
              </Select>
            </Box>
            {/* Upcoming Schedule */}
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#64748b", mb: 0.5, textTransform: "uppercase", letterSpacing: "0.05em" }}>Upcoming Schedule</Typography>
              <Select size="small" value={dateFilter} onChange={(e) => { setDateFilter(e.target.value); setStartDate(""); setEndDate(""); }} displayEmpty
                sx={{ minWidth: 168, borderRadius: 2, fontSize: 13, height: 36, bgcolor: "#fff", "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER } }}>
                <MenuItem value=""><em>All Upcoming</em></MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="tomorrow">Tomorrow</MenuItem>
                <MenuItem value="this_week">This Week</MenuItem>
                <MenuItem value="next_week">Next Week</MenuItem>
                <MenuItem value="this_month">This Month</MenuItem>
                <MenuItem value="next_month">Next Month</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
              {dateFilter && dateFilter !== "custom" && (
                <Typography sx={{ fontSize: 10, color: "#2f3891", mt: 0.4 }}>Showing upcoming trips only</Typography>
              )}
            </Box>
            {dateFilter === "custom" && (
              <>
                <Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#64748b", mb: 0.5, textTransform: "uppercase", letterSpacing: "0.05em" }}>From</Typography>
                  <TextField type="date" size="small" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: dayjs().format("YYYY-MM-DD") }}
                    sx={{ width: 155, "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, height: 36 } }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#64748b", mb: 0.5, textTransform: "uppercase", letterSpacing: "0.05em" }}>To</Typography>
                  <TextField type="date" size="small" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: startDate || dayjs().format("YYYY-MM-DD") }}
                    sx={{ width: 155, "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, height: 36 } }} />
                </Box>
              </>
            )}
            {(selectedDriver || dateFilter) && (
              <Button size="small"
                onClick={() => { setSelectedDriver(""); setDateFilter(""); setStartDate(""); setEndDate(""); }}
                sx={{ height: 36, borderRadius: 2, textTransform: "none", fontSize: 12, color: "#64748b", border: `1px solid ${BORDER}` }}>
                Clear
              </Button>
            )}
          </Box>
        </Card>

        {/* ── Driver sections ── */}
        {filteredDriverTrips.length === 0 ? (
          <Card sx={{ borderRadius: 3, boxShadow: "0 1px 6px rgba(0,0,0,0.05)", border: `1px solid ${BORDER}` }}>
            <Box sx={{ py: 8, textAlign: "center" }}>
              <LocalShippingOutlinedIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 1.5 }} />
              <Typography sx={{ color: "#94a3b8", fontSize: 14, fontWeight: 500 }}>No trips found for the selected filters</Typography>
            </Box>
          </Card>
        ) : (
          filteredDriverTrips.map((driver) => {
            const collapsed   = collapsedDrivers[driver.driverId];
            const allSel      = driver.trips.every((t, i) => selectedTrips[tk(driver.driverId, t, i)]);
            const anySel      = anySelected(driver.driverId, driver.trips);

            const driverFullObj   = drivers.find((dr: any) => String(dr.id) === driver.driverId);
            const driverConflicts = driverFullObj
              ? driver.trips.filter((t) => {
                  const c = checkTripAvailability(driverFullObj, t);
                  return !c.valid && c.hasSchedule;
                }).length
              : 0;

            return (
              <Card key={driver.driverId} sx={{ borderRadius: 3, boxShadow: "0 1px 8px rgba(0,0,0,0.07)", border: driverConflicts > 0 ? "1px solid #fca5a5" : `1px solid ${BORDER}`, mb: 2, overflow: "hidden" }}>

                {/* ── Driver header card ── */}
                <Box
                  onClick={() => toggleDriver(driver.driverId)}
                  sx={{ px: 2, py: 1.5, borderBottom: collapsed ? "none" : `1px solid ${BORDER}`, display: "flex", alignItems: "center", gap: 1.5, background: driverConflicts > 0 ? "linear-gradient(135deg,#fff5f5,#fef2f2)" : "linear-gradient(135deg,#f8fafc,#f1f5f9)", cursor: "pointer" }}>
                  <Avatar sx={{ width: 38, height: 38, bgcolor: BRAND, fontSize: 13, fontWeight: 700 }}>
                    {initials(driver.driverFirstName, driver.driverLastName)}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>
                      {cap(driver.driverFirstName)} {cap(driver.driverLastName)}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 0.25 }}>
                      {driver.driverEmail     && <Typography sx={{ fontSize: 11, color: "#64748b" }}>✉ {driver.driverEmail}</Typography>}
                      {driver.driverMobileNum && <Typography sx={{ fontSize: 11, color: "#64748b" }}>📞 {driver.driverMobileNum}</Typography>}
                    </Box>
                  </Box>
                  <Chip label={`${driver.trips.length} trip${driver.trips.length !== 1 ? "s" : ""}`} size="small"
                    sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: "#eef2ff", color: BRAND_DARK, border: `1px solid ${BRAND}33` }} />
                  {driverConflicts > 0 && (
                    <Tooltip title={`${driverConflicts} trip${driverConflicts > 1 ? "s" : ""} outside working hours`} arrow>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, px: 0.875, py: 0.3,
                        bgcolor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 1.5, cursor: "default" }}>
                        <WarningAmberIcon sx={{ fontSize: 12, color: "#dc2626" }} />
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#dc2626" }}>
                          {driverConflicts} conflict{driverConflicts > 1 ? "s" : ""}
                        </Typography>
                      </Box>
                    </Tooltip>
                  )}
                  {anySel && (
                    <Button size="small" variant="contained"
                      startIcon={loading ? <CircularProgress size={12} sx={{ color: "#fff" }} /> : <EmailIcon sx={{ fontSize: 14 }} />}
                      disabled={loading}
                      onClick={(e) => { e.stopPropagation(); sendEmailForDriver(driver); }}
                      sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, fontSize: 12, bgcolor: BRAND, "&:hover": { bgcolor: BRAND_DARK }, px: 1.5, py: 0.5 }}>
                      Send Email
                    </Button>
                  )}
                  <IconButton size="small" sx={{ color: "#94a3b8" }}>
                    {collapsed ? <ExpandMoreIcon sx={{ fontSize: 18 }} /> : <ExpandLessIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                </Box>

                {/* ── Trip table ── */}
                <Collapse in={!collapsed} timeout="auto">
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ ...thSx, width: 36, px: 1 }}>
                            <CustomCheckbox name="sa" checked={allSel} onChange={() => handleSelectAll(driver.driverId, driver.trips)} />
                          </TableCell>
                          <TableCell sx={thSx}>#</TableCell>
                          <TableCell sx={thSx}>Invoice</TableCell>
                          <TableCell sx={thSx}>Bus / Service</TableCell>
                          <TableCell sx={thSx}>Type</TableCell>
                          <TableCell sx={thSx}>Route</TableCell>
                          <TableCell sx={{ ...thSx, minWidth: 200 }}>Pickup Details</TableCell>
                          <TableCell sx={{ ...thSx, minWidth: 200 }}>Drop-off Details</TableCell>
                          <TableCell sx={{ ...thSx, minWidth: 140 }}>Working Hours</TableCell>
                          <TableCell sx={thSx}>Status</TableCell>
                          <TableCell sx={{ ...thSx, textAlign: "right" }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {driver.trips.map((trip, idx) => {
                          const key        = tk(driver.driverId, trip, idx);
                          const tt         = transferLabel(trip.transferType);
                          const st         = tripStatus(trip.departureDateTime, trip.isPaid, trip.isCancelled);
                          const isToday    = dayjs(trip.departureDateTime).isSame(dayjs(), "day");
                          const fullDriver = drivers.find((dr: any) => String(dr.id) === driver.driverId);
                          const avCheck: AvailCheck = fullDriver
                            ? checkTripAvailability(fullDriver, trip)
                            : { valid: true, reason: "", slots: [], hasSchedule: false };

                          const isConflict = !avCheck.valid && avCheck.hasSchedule;
                          return (
                            <TableRow key={key}
                              sx={{
                                "&:hover": { bgcolor: isConflict ? "#fef2f2" : "#f8fafc" },
                                "&:last-child td": { borderBottom: 0 },
                                bgcolor: isConflict ? "#fff5f5"
                                       : isToday    ? "#fffbeb"
                                       : "transparent",
                                borderLeft: isConflict ? "3px solid #ef4444" : "3px solid transparent",
                              }}>

                              {/* Checkbox */}
                              <TableCell sx={{ ...tdSx, px: 1 }}>
                                <CustomCheckbox name="cb" checked={!!selectedTrips[key]} onChange={() => handleTripCheckbox(key)} />
                              </TableCell>

                              {/* # */}
                              <TableCell sx={{ ...tdSx, color: "#94a3b8", width: 32 }}>{idx + 1}</TableCell>

                              {/* Invoice */}
                              <TableCell sx={tdSx}>
                                {trip.InvoiceNo ? (
                                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: BRAND, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                                    onClick={() => navigate(`/work-order/history/${trip.work_order_id}`)}>
                                    {trip.InvoiceNo}
                                  </Typography>
                                ) : <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>—</Typography>}
                              </TableCell>

                              {/* Bus */}
                              <TableCell sx={tdSx}>
                                {trip.productName ? (
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                    <DirectionsBusOutlinedIcon sx={{ fontSize: 14, color: "#64748b" }} />
                                    <Typography sx={{ fontSize: 12, color: "#374151" }}>{trip.productName}</Typography>
                                  </Box>
                                ) : <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>—</Typography>}
                              </TableCell>

                              {/* Transfer type */}
                              <TableCell sx={tdSx}>
                                <Chip label={tt.label} size="small"
                                  sx={{ height: 20, fontSize: 10, fontWeight: 700, color: tt.color, bgcolor: tt.bg, border: `1px solid ${tt.color}33` }} />
                              </TableCell>

                              {/* Route: From → To */}
                              <TableCell sx={tdSx}>
                                {trip.transferTypeFrom || trip.transferTypeTo ? (
                                  <Box>
                                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#374151" }}>{trip.transferTypeFrom || "—"}</Typography>
                                    <Typography sx={{ fontSize: 10, color: "#94a3b8", mx: 0.25 }}>↓</Typography>
                                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#374151" }}>{trip.transferTypeTo || "—"}</Typography>
                                  </Box>
                                ) : <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>—</Typography>}
                              </TableCell>

                              {/* Pickup: full address + time */}
                              <TableCell sx={tdSx}>
                                <Box sx={{ bgcolor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 1.5, px: 1.25, py: 0.75 }}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.4 }}>
                                    <FmdGoodOutlinedIcon sx={{ fontSize: 11, color: "#16a34a" }} />
                                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#16a34a", textTransform: "uppercase", letterSpacing: "0.05em" }}>Pickup</Typography>
                                  </Box>
                                  {trip.departureAddress ? (
                                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#0f172a", lineHeight: 1.35 }}>{trip.departureAddress}</Typography>
                                  ) : (
                                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>Address not specified</Typography>
                                  )}
                                  {trip.departureDateTime && dayjs(trip.departureDateTime).isValid() && (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
                                      <Typography sx={{ fontSize: 11, color: "#374151" }}>
                                        {dayjs(trip.departureDateTime).format("DD MMM YYYY")}
                                      </Typography>
                                      <Typography sx={{ fontSize: 11, fontWeight: 800, color: BRAND }}>
                                        {dayjs(trip.departureDateTime).format("h:mm A")}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </TableCell>

                              {/* Drop-off: full address + time */}
                              <TableCell sx={tdSx}>
                                <Box sx={{ bgcolor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 1.5, px: 1.25, py: 0.75 }}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.4 }}>
                                    <FmdGoodOutlinedIcon sx={{ fontSize: 11, color: "#dc2626" }} />
                                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#dc2626", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                      {trip.transferType === "1" ? "Return / Drop-off" : "Drop-off"}
                                    </Typography>
                                  </Box>
                                  {trip.destinationAddress ? (
                                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#0f172a", lineHeight: 1.35 }}>{trip.destinationAddress}</Typography>
                                  ) : (
                                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>Address not specified</Typography>
                                  )}
                                  {trip.returnDateTime && dayjs(trip.returnDateTime).isValid() && (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
                                      <Typography sx={{ fontSize: 11, color: "#374151" }}>
                                        {dayjs(trip.returnDateTime).format("DD MMM YYYY")}
                                      </Typography>
                                      <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#2f3891" }}>
                                        {dayjs(trip.returnDateTime).format("h:mm A")}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </TableCell>

                              {/* Working Hours column — always visible */}
                              <TableCell sx={tdSx}>
                                {!avCheck.hasSchedule ? (
                                  /* No schedule configured for this day */
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5,
                                    px: 0.875, py: 0.375, bgcolor: "#f8fafc",
                                    border: "1px solid #e2e8f0", borderRadius: 1.5, width: "fit-content" }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#cbd5e1", flexShrink: 0 }} />
                                    <Typography sx={{ fontSize: 10, color: "#94a3b8", fontWeight: 600 }}>
                                      No Schedule
                                    </Typography>
                                  </Box>
                                ) : isConflict ? (
                                  /* Trip is OUTSIDE working hours */
                                  <Tooltip title={avCheck.reason} arrow placement="left">
                                    <Box>
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5,
                                        px: 0.875, py: 0.375, bgcolor: "#fef2f2",
                                        border: "1px solid #fecaca", borderRadius: 1.5,
                                        width: "fit-content", cursor: "default", mb: 0.5 }}>
                                        <WarningAmberIcon sx={{ fontSize: 11, color: "#dc2626" }} />
                                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#dc2626" }}>
                                          Outside Hours
                                        </Typography>
                                      </Box>
                                      {avCheck.slots.map((s, i) => (
                                        <Typography key={i} sx={{ fontSize: 10, color: "#dc2626", fontWeight: 600 }}>
                                          {s.startTime}–{s.endTime}
                                        </Typography>
                                      ))}
                                    </Box>
                                  </Tooltip>
                                ) : (
                                  /* Trip is WITHIN working hours */
                                  <Box>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5,
                                      px: 0.875, py: 0.375, bgcolor: "#f0fdf4",
                                      border: "1px solid #bbf7d0", borderRadius: 1.5,
                                      width: "fit-content", mb: 0.5 }}>
                                      <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#22c55e", flexShrink: 0 }} />
                                      <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#16a34a" }}>
                                        Within Hours
                                      </Typography>
                                    </Box>
                                    {avCheck.slots.map((s, i) => (
                                      <Typography key={i} sx={{ fontSize: 10, color: "#16a34a", fontWeight: 600 }}>
                                        {s.startTime}–{s.endTime}
                                      </Typography>
                                    ))}
                                  </Box>
                                )}
                              </TableCell>

                              {/* Status */}
                              <TableCell sx={tdSx}>
                                <Chip label={st.label} size="small"
                                  sx={{ height: 20, fontSize: 10, fontWeight: 700, color: st.color, bgcolor: st.bg, border: `1px solid ${st.color}33` }} />
                                {isToday && (
                                  <Chip label="Today" size="small" sx={{ mt: 0.5, height: 18, fontSize: 9, fontWeight: 700, color: "#92400e", bgcolor: "#fef3c7", border: "1px solid #fde68a", display: "flex" }} />
                                )}
                              </TableCell>

                              {/* Action */}
                              <TableCell sx={{ ...tdSx, textAlign: "right" }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, justifyContent: "flex-end" }}>
                                  <Tooltip title="View trip details" arrow>
                                    <IconButton size="small"
                                      onClick={() => navigate(`/work-order/history/${trip.work_order_id}`)}
                                      sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: "#f0f9ff", color: "#0ea5e9", "&:hover": { bgcolor: "#e0f2fe" } }}>
                                      <VisibilityOutlinedIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                  {st.label === "Confirmed" && (
                                    <Tooltip title="Complete trip & record driver payment" arrow>
                                      <IconButton size="small"
                                        onClick={() => openCompleteTrip(trip, driver)}
                                        sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: "#f0fdf4", color: "#16a34a", "&:hover": { bgcolor: "#dcfce7" } }}>
                                        <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapse>
              </Card>
            );
          })
        )}
      </Box>

      {/* ── Complete Trip Modal ─────────────────────────────────────────────── */}
      <Dialog open={completeTripOpen} onClose={() => !ctSaving && setCompleteTripOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 20, color: "#16a34a" }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>Complete Trip & Record Payment</Typography>
              <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                {completingTrip?.driverData.driverName} — {completingTrip?.trip.InvoiceNo || "No invoice"}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          {completingTrip && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Trip info */}
              <Box sx={{ bgcolor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 2, p: 2 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", mb: 1 }}>Trip Info</Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 11, color: "#64748b" }}>Departure</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>
                      {completingTrip.trip.departureDateTime
                        ? dayjs(completingTrip.trip.departureDateTime).format("DD MMM YYYY h:mm A")
                        : "—"}
                    </Typography>
                    {completingTrip.trip.departureAddress && (
                      <Typography sx={{ fontSize: 11, color: "#64748b", mt: 0.25 }}>
                        {completingTrip.trip.departureAddress}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 11, color: "#64748b" }}>Return</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>
                      {completingTrip.trip.returnDateTime
                        ? dayjs(completingTrip.trip.returnDateTime).format("DD MMM YYYY h:mm A")
                        : "—"}
                    </Typography>
                    {completingTrip.trip.destinationAddress && (
                      <Typography sx={{ fontSize: 11, color: "#64748b", mt: 0.25 }}>
                        {completingTrip.trip.destinationAddress}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Typography sx={{ fontSize: 11, color: "#64748b" }}>Route</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>
                      {(completingTrip.trip.departureAddress || completingTrip.trip.transferTypeFrom) &&
                       (completingTrip.trip.destinationAddress || completingTrip.trip.transferTypeTo)
                        ? `${completingTrip.trip.departureAddress || completingTrip.trip.transferTypeFrom} → ${completingTrip.trip.destinationAddress || completingTrip.trip.transferTypeTo}`
                        : completingTrip.trip.departureAddress || completingTrip.trip.destinationAddress || "—"}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Salary calculation */}
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField size="small" fullWidth label="Working Hours" type="number"
                    value={ctHoursWorked}
                    onChange={(e) => setCtHoursWorked(e.target.value)}
                    helperText="Auto-calculated, editable"
                    InputProps={{ endAdornment: <InputAdornment position="end">hrs</InputAdornment> }}
                    inputProps={{ min: 0, step: 0.25 }} />
                </Grid>
                <Grid item xs={4}>
                  <TextField size="small" fullWidth label="Hourly Rate ($)" type="number"
                    value={ctHourlyRate}
                    onChange={(e) => setCtHourlyRate(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    inputProps={{ min: 0, step: 0.5 }} />
                </Grid>
                <Grid item xs={4}>
                  <TextField size="small" fullWidth label="Tips ($)" type="number"
                    value={ctTips}
                    onChange={(e) => setCtTips(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    inputProps={{ min: 0, step: 1 }} />
                </Grid>
              </Grid>

              {/* Payment summary */}
              <Box sx={{ bgcolor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 2, p: 2 }}>
                <Grid container spacing={1}>
                  <Grid item xs={4} sx={{ textAlign: "center" }}>
                    <Typography sx={{ fontSize: 11, color: "#64748b" }}>Base Salary</Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>${ctBaseSalary.toFixed(2)}</Typography>
                    <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>{parseFloat(ctHoursWorked) || 0}h × ${parseFloat(ctHourlyRate || "0").toFixed(2)}</Typography>
                  </Grid>
                  <Grid item xs={4} sx={{ textAlign: "center" }}>
                    <Typography sx={{ fontSize: 11, color: "#64748b" }}>Tips</Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>${(parseFloat(ctTips) || 0).toFixed(2)}</Typography>
                  </Grid>
                  <Grid item xs={4} sx={{ textAlign: "center" }}>
                    <Typography sx={{ fontSize: 11, color: "#64748b" }}>Total Payment</Typography>
                    <Typography sx={{ fontSize: 20, fontWeight: 900, color: "#16a34a" }}>${ctTotal.toFixed(2)}</Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Comments */}
              <TextField size="small" fullWidth multiline rows={2} label="Driver Comments (optional)"
                value={ctDriverComments} onChange={(e) => setCtDriverComments(e.target.value)}
                placeholder="Any notes from the driver about the trip..." />
              <TextField size="small" fullWidth multiline rows={2} label="Driver Instructions (Admin → Driver)"
                value={ctAdminComments} onChange={(e) => setCtAdminComments(e.target.value)}
                placeholder="Instructions or notes from admin to driver..." />
              <TextField size="small" fullWidth multiline rows={2} label="Customer Feedback (optional)"
                value={ctCustomerFeedback} onChange={(e) => setCtCustomerFeedback(e.target.value)}
                placeholder="Feedback or comments from the customer about this trip..." />
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button variant="outlined" size="small" onClick={() => setCompleteTripOpen(false)} disabled={ctSaving}
            sx={{ borderRadius: 2, textTransform: "none" }}>
            Cancel
          </Button>
          <Button variant="contained" size="small" onClick={handleCompleteTripSave}
            disabled={ctSaving || (parseFloat(ctHoursWorked) || 0) <= 0}
            startIcon={ctSaving ? <CircularProgress size={14} /> : <CheckCircleOutlineIcon />}
            sx={{ borderRadius: 2, textTransform: "none", bgcolor: "#16a34a", "&:hover": { bgcolor: "#15803d" } }}>
            {ctSaving ? "Saving..." : `Save Payment • $${ctTotal.toFixed(2)}`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DriverTripsComponent;
